/* Optional mixed-model extension for the unmodified original .bk-set bundle.
 * Same-model selections keep their original click handler and original pricing.
 * Mixed selections make one Shopify add request; no retries, pixels or custom
 * purchase events are sent here.
 */
(function (global, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!global || !global.document || global.__swKissenMixInstalled) return;
  global.__swKissenMixInstalled = true;
  api.boot(global.document, global);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const ALLOWED_QUANTITIES = [1, 3, 6];

  function variantFor(products, slot) {
    const product = products.find((item) => item.handle === slot?.handle);
    const variant = product?.variants?.find((item) => String(item.id) === String(slot?.variantId));
    return { product, variant };
  }

  function defaultVariant(product) {
    return product?.variants?.find((variant) => variant.available === true) || product?.variants?.[0];
  }

  // Never truncate memory: switching 6 -> 3 -> 6 restores every chosen slot.
  function ensureSlots(memory, quantity, baseline) {
    if (!ALLOWED_QUANTITIES.includes(quantity)) throw new Error('Ungültige Kissenanzahl.');
    const next = memory.map((slot) => ({ ...slot }));
    while (next.length < quantity) next.push({ ...baseline });
    return next;
  }

  function needsCustomAdd(memory, quantity, baseline) {
    return quantity > 1 && memory.slice(0, quantity).some((slot) =>
      slot.handle !== baseline.handle || String(slot.variantId) !== String(baseline.variantId));
  }

  function validateSelection(products, slots, quantity) {
    if (![3, 6].includes(quantity) || slots.length !== quantity) {
      return { valid: false, message: 'Bitte wähle alle Kissen deines Sets aus.' };
    }
    const lines = [];
    for (let index = 0; index < slots.length; index += 1) {
      const { product, variant } = variantFor(products, slots[index]);
      if (!product || !variant || !/^\d+$/.test(String(variant.id)) ||
          !Number.isSafeInteger(Number(variant.id)) || Number(variant.id) <= 0 ||
          !Number.isSafeInteger(variant.price) || variant.price < 0) {
        return { valid: false, message: 'Bitte prüfe die Auswahl für Kissen ' + (index + 1) + '.' };
      }
      if (variant.available !== true) {
        return { valid: false, message: product.title + ' ist in dieser Ausführung aktuell ausverkauft. Bitte wähle ein anderes Kissen.' };
      }
      lines.push({ id: String(variant.id), price: variant.price });
    }
    return { valid: true, lines };
  }

  function aggregateItems(lines) {
    const items = new Map();
    lines.forEach((line) => {
      if (!items.has(line.id)) items.set(line.id, { id: line.id, quantity: 0 });
      items.get(line.id).quantity += 1;
    });
    return Array.from(items.values());
  }

  function calculateMixedOffer(lines) {
    if (![3, 6].includes(lines.length)) throw new Error('Ungültige Kissenanzahl.');
    const prices = lines.map((line) => line.price);
    if (prices.some((price) => !Number.isSafeInteger(price) || price < 0)) throw new Error('Ungültiger Preis.');
    const freeQuantity = Math.floor(prices.length / 3);
    const subtotal = prices.reduce((sum, price) => sum + price, 0);
    const savings = prices.slice().sort((a, b) => a - b).slice(0, freeQuantity).reduce((sum, price) => sum + price, 0);
    return { subtotal, savings, total: subtotal - savings, freeQuantity };
  }

  function confirmedItems(result, expected) {
    if (!Array.isArray(result?.items)) return false;
    // Shopify can return the new cart-line quantity, which includes quantities
    // already present. Check original variant identities without equating it
    // to the request's added quantity.
    return expected.every((item) => result.items.some((line) =>
      String(line.variant_id || line.id) === item.id && Number(line.quantity) >= item.quantity));
  }

  class MixController {
    constructor(picker, productRoot, wrap, products, view) {
      this.picker = picker;
      this.productRoot = productRoot;
      this.wrap = wrap;
      this.products = products;
      this.view = view;
      this.document = picker.ownerDocument;
      this.atc = wrap.querySelector('.bk-atc');
      this.cards = Array.from(wrap.querySelectorAll('.bk-set'));
      this.sets = wrap.querySelector('.bk-sets');
      this.price = productRoot.querySelector('[data-product-price]');
      this.savings = wrap.querySelector('.bk-savings');
      this.handle = picker.dataset.currentProductHandle || picker.dataset.productHandle;
      this.product = products.find((item) => item.handle === this.handle);
      this.quantity = 1;
      this.memory = [];
      this.busy = false;
      this.uncertain = false;
      this.priceOwned = false;
      this.restoring = false;
      this.destroyed = false;
      const nativeText = this.atc.firstChild?.textContent || this.atc.textContent;
      this.atcLabel = picker.dataset.mixAtcLabel || nativeText.split('·')[0].trim() || 'In den Warenkorb';
      this.currency = picker.dataset.currency || 'EUR';
      this.locale = picker.dataset.locale || 'de-DE';
      try { this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }); }
      catch (_) { this.money = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }); }
      const routeRoot = view.Shopify?.routes?.root || '/';
      this.rootPath = routeRoot.endsWith('/') ? routeRoot : routeRoot + '/';
      this.cartUrl = this.rootPath + 'cart';
      this.addUrl = this.rootPath + 'cart/add.js';
      this.build();
      this.onCapture = this.captureClick.bind(this);
      this.onClick = this.bubbleClick.bind(this);
      this.onChange = this.change.bind(this);
      wrap.addEventListener('click', this.onCapture, true);
      wrap.addEventListener('click', this.onClick);
      wrap.addEventListener('change', this.onChange);
      this.syncFromNative();
    }

    element(tag, className, text) {
      const node = this.document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    build() {
      this.details = this.element('details', 'sw-kissen-mix');
      this.details.hidden = true;
      const summary = this.element('summary', '', this.picker.dataset.mixHeading || 'Kissen einzeln zusammenstellen');
      summary.append(this.element('span', 'sw-kissen-mix__plus', '+'));
      summary.lastChild.setAttribute('aria-hidden', 'true');
      this.details.append(summary);
      this.details.append(this.element('p', 'sw-kissen-mix__intro', 'Optional: Wähle für jedes Kissen ein Modell. Ohne Änderung erhältst du das oben gezeigte Modell.'));
      this.slotHost = this.element('div', 'sw-kissen-mix__slots');
      this.details.append(this.slotHost);
      this.estimate = this.element('p', 'sw-kissen-mix__estimate', 'Voraussichtlicher Preis für diese Auswahl. Bereits vorhandene Warenkorbartikel können die Ersparnis verändern. Der endgültige Rabatt wird im Warenkorb berechnet.');
      this.estimate.hidden = true;
      this.details.append(this.estimate);
      const reset = this.element('button', 'sw-kissen-mix__reset', 'Alle Kissen wie oben auswählen');
      reset.type = 'button';
      reset.dataset.swKissenMixReset = '';
      this.details.append(reset);
      this.sets.insertAdjacentElement('afterend', this.details);
      this.error = this.element('div', 'sw-kissen-mix-status');
      this.error.hidden = true;
      this.error.setAttribute('role', 'alert');
      this.error.setAttribute('tabindex', '-1');
      this.atc.insertAdjacentElement('beforebegin', this.error);
    }

    currentCard() {
      return this.wrap.querySelector('.bk-set.bk-active') || this.cards[0];
    }

    baseline() {
      const card = this.currentCard();
      return { handle: this.handle, variantId: String(card?.dataset.variantId || defaultVariant(this.product)?.id || '') };
    }

    subscriptionActive() {
      return !!this.wrap.querySelector('.bk-sub input[value="subscribe"]:checked');
    }

    supportedCard() {
      const card = this.currentCard();
      return !!card && [3, 6].includes(this.quantity) &&
        Number(card.dataset.variantQty || this.quantity) === this.quantity &&
        !!variantFor(this.products, this.baseline()).variant && !this.subscriptionActive();
    }

    isMixed() {
      return this.supportedCard() && needsCustomAdd(this.memory, this.quantity, this.baseline());
    }

    syncFromNative() {
      const card = this.currentCard();
      const quantity = Number(card?.dataset.total);
      if (!ALLOWED_QUANTITIES.includes(quantity)) {
        this.details.hidden = true;
        return;
      }
      this.quantity = quantity;
      this.memory = ensureSlots(this.memory, quantity, this.baseline());
      this.details.hidden = !this.supportedCard();
      if (this.details.hidden) this.details.open = false;
      this.renderSlots();
      this.refreshMixed();
    }

    renderSlots() {
      const fragment = this.document.createDocumentFragment();
      this.memory.slice(0, this.quantity).forEach((slot, index) => {
        const { product, variant } = variantFor(this.products, slot);
        const row = this.element('div', 'sw-kissen-mix__slot');
        const image = this.element('img', 'sw-kissen-mix__image');
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        image.alt = product?.images?.[0]?.alt || product?.title || '';
        if (product?.images?.[0]?.src) {
          try {
            const src = new URL(product.images[0].src, this.view.location.href);
            src.searchParams.set('width', '160');
            image.src = src.href;
          } catch (_) { image.hidden = true; }
        } else image.hidden = true;
        row.append(image);
        const fields = this.element('div', 'sw-kissen-mix__fields');
        const modelId = this.wrap.id + '-mix-' + index + '-model';
        const modelLabel = this.element('label', '', 'Kissen ' + (index + 1));
        modelLabel.htmlFor = modelId;
        const models = this.element('select');
        models.id = modelId;
        models.dataset.swKissenMixSlot = String(index);
        models.dataset.swKissenMixField = 'model';
        this.products.forEach((item) => {
          const option = this.element('option', '', item.shortLabel || item.title);
          option.value = item.handle;
          option.selected = item.handle === slot.handle;
          option.disabled = !item.variants?.some((candidate) => candidate.available === true);
          if (option.disabled) option.textContent += ' · Ausverkauft';
          models.append(option);
        });
        fields.append(modelLabel, models);
        if (product?.variants?.length > 1 || (variant && variant.title !== 'Default Title')) {
          const variantId = this.wrap.id + '-mix-' + index + '-variant';
          const variantLabel = this.element('label', '', 'Ausführung');
          variantLabel.htmlFor = variantId;
          const variants = this.element('select');
          variants.id = variantId;
          variants.dataset.swKissenMixSlot = String(index);
          variants.dataset.swKissenMixField = 'variant';
          product.variants.forEach((candidate) => {
            const option = this.element('option', '', candidate.title);
            option.value = String(candidate.id);
            option.selected = String(candidate.id) === String(slot.variantId);
            option.disabled = candidate.available !== true;
            if (option.disabled) option.textContent += ' · Ausverkauft';
            variants.append(option);
          });
          fields.append(variantLabel, variants);
        }
        row.append(fields);
        fragment.append(row);
      });
      this.slotHost.replaceChildren(fragment);
    }

    showError(message, uncertain) {
      this.error.replaceChildren(this.element('p', '', message));
      if (uncertain) {
        const link = this.element('a', '', 'Warenkorb prüfen');
        link.href = this.cartUrl;
        this.error.append(link);
      }
      this.error.hidden = false;
    }

    clearError() {
      if (!this.uncertain) this.error.hidden = true;
    }

    restoreNative() {
      if (!this.priceOwned) return;
      this.priceOwned = false;
      this.estimate.hidden = true;
      const card = this.currentCard();
      if (!card || this.restoring) return;
      // Re-run only the original selected-card UI calculation. The original
      // card handler neither submits nor changes the selected products.
      this.restoring = true;
      try { card.click(); } finally { this.restoring = false; }
    }

    refreshMixed() {
      const mixed = this.isMixed();
      this.estimate.hidden = !mixed;
      if (!mixed) {
        this.restoreNative();
        if (this.uncertain) this.atc.disabled = true;
        return;
      }
      const validated = validateSelection(this.products, this.memory.slice(0, this.quantity), this.quantity);
      if (!validated.valid) {
        this.priceOwned = true;
        this.showError(validated.message, false);
        this.atc.disabled = true;
        return;
      }
      const totals = calculateMixedOffer(validated.lines);
      this.priceOwned = true;
      if (this.price) this.price.textContent = this.money.format(totals.total / 100);
      const compare = this.productRoot.querySelector('[data-compare-price]') ||
        this.productRoot.querySelector('[id="bk-strike-' + this.wrap.id + '"]');
      if (compare) {
        compare.textContent = this.money.format(totals.subtotal / 100);
        compare.style.display = '';
        const holder = compare.closest('[data-compare-price-wrapper]');
        if (holder) holder.style.display = '';
      }
      if (this.price) this.price.classList.add('sale-price');
      if (this.savings) {
        this.savings.textContent = '🎁 ' + totals.freeQuantity + ' Kissen geschenkt — voraussichtliche Ersparnis ' + this.money.format(totals.savings / 100);
        this.savings.style.display = '';
      }
      if (!this.busy) {
        this.atc.replaceChildren(this.document.createTextNode(this.atcLabel + ' '));
        const compareLabel = this.element('span', 'sw-kissen-mix-price-compare', this.money.format(totals.subtotal / 100));
        const totalLabel = this.element('strong', '', this.money.format(totals.total / 100));
        this.atc.append(compareLabel, this.document.createTextNode(' '), totalLabel);
      }
      this.atc.disabled = this.busy || this.uncertain;
    }

    captureClick(event) {
      if (this.restoring) return;
      const atc = event.target.closest?.('.bk-atc');
      const card = event.target.closest?.('.bk-set');
      if (this.busy || this.uncertain) {
        if ((atc && this.wrap.contains(atc)) || (card && this.wrap.contains(card))) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }
      if (!atc || atc !== this.atc || !this.isMixed()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addMixed();
    }

    bubbleClick(event) {
      if (this.restoring || this.busy || this.uncertain) return;
      if (event.target.closest?.('.bk-set')?.closest('div[id^="sw-bk-"]') === this.wrap) {
        this.clearError();
        this.syncFromNative();
      } else if (event.target.closest?.('[data-sw-kissen-mix-reset]')) {
        this.memory = Array.from({ length: Math.max(6, this.memory.length) }, () => ({ ...this.baseline() }));
        this.clearError();
        this.renderSlots();
        this.refreshMixed();
      }
    }

    change(event) {
      if (this.busy || this.uncertain) return;
      const control = event.target;
      if (control.matches?.('.bk-sub input')) {
        this.syncFromNative();
        return;
      }
      if (!control.matches?.('[data-sw-kissen-mix-slot]')) return;
      const index = Number(control.dataset.swKissenMixSlot);
      if (!Number.isInteger(index) || index < 0 || index >= this.quantity) return;
      const slot = this.memory[index];
      if (control.dataset.swKissenMixField === 'model') {
        const product = this.products.find((item) => item.handle === control.value);
        const variant = defaultVariant(product);
        if (!product || !variant) return;
        this.memory[index] = { handle: product.handle, variantId: String(variant.id) };
      } else {
        const { product } = variantFor(this.products, slot);
        if (!product?.variants?.some((variant) => String(variant.id) === control.value)) return;
        slot.variantId = control.value;
      }
      this.clearError();
      this.renderSlots();
      this.refreshMixed();
      // Re-rendering options must not leave keyboard users at the document top.
      const replacement = this.slotHost.querySelector('[data-sw-kissen-mix-slot="' + index + '"][data-sw-kissen-mix-field="' + control.dataset.swKissenMixField + '"]');
      replacement?.focus({ preventScroll: true });
    }

    lock(locked) {
      this.busy = locked;
      this.details.setAttribute('aria-busy', String(locked));
      this.details.querySelectorAll('select,button').forEach((control) => { control.disabled = locked || this.uncertain; });
      this.atc.disabled = locked || this.uncertain;
      if (locked) this.atc.textContent = 'Wird hinzugefügt …';
    }

    async addMixed() {
      if (this.busy || this.uncertain || !this.isMixed()) return;
      const validated = validateSelection(this.products, this.memory.slice(0, this.quantity), this.quantity);
      if (!validated.valid) {
        this.showError(validated.message, false);
        this.atc.disabled = true;
        this.error.focus({ preventScroll: false });
        return;
      }
      const items = aggregateItems(validated.lines);
      this.clearError();
      this.lock(true);
      try {
        const response = await this.view.fetch(this.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        let result;
        try { result = await response.json(); } catch (_) {
          throw new Error('Die Bestätigung konnte nicht gelesen werden. Bitte prüfe zuerst deinen Warenkorb.');
        }
        if (!response.ok || (result?.status && Number(result.status) >= 400)) {
          // Shopify inventory failures can partially affect a cart. Do not
          // silently retry a mixed request or assume that nothing was added.
          const description = typeof result?.description === 'string' ? result.description : 'Mindestens ein Kissen ist in dieser Menge nicht verfügbar.';
          throw new Error(description + ' Bitte prüfe deinen Warenkorb, bevor du erneut bestellst.');
        }
        if (!confirmedItems(result, items)) {
          throw new Error('Die Bestätigung ist unvollständig. Bitte prüfe zuerst deinen Warenkorb.');
        }
        // This is the event dispatched by the unmodified original handler.
        // Existing fetch wrappers retain ownership of drawer/popup behavior.
        this.document.dispatchEvent(new this.view.CustomEvent('cart:updated'));
      } catch (error) {
        this.uncertain = true;
        if (!this.destroyed) {
          this.showError(error?.message || 'Die Verbindung wurde unterbrochen. Bitte prüfe zuerst deinen Warenkorb.', true);
          this.error.focus({ preventScroll: false });
        }
      } finally {
        if (!this.destroyed) {
          this.lock(false);
          this.refreshMixed();
        }
      }
    }

    destroy() {
      this.destroyed = true;
      this.wrap.removeEventListener('click', this.onCapture, true);
      this.wrap.removeEventListener('click', this.onClick);
      this.wrap.removeEventListener('change', this.onChange);
      this.details.remove();
      this.error.remove();
      // An in-flight cart request is intentionally not aborted or retried.
    }
  }

  function boot(document, view) {
    const instances = new Map();
    function init(scope) {
      const pickers = Array.from(scope.querySelectorAll?.('.sw-kissen-models') || []);
      if (scope.matches?.('.sw-kissen-models')) pickers.unshift(scope);
      pickers.forEach((picker) => {
        if (instances.has(picker) || picker.dataset.mixEnabled === 'false') return;
        const productRoot = picker.closest('[data-section-type="product"]');
        const atc = productRoot?.querySelector('.bk-atc');
        const wrap = atc?.closest('div[id^="sw-bk-"]');
        const data = picker.querySelector('script[data-sw-kissen-products]');
        if (!wrap || !data || !wrap.querySelector('.bk-sets')) return;
        let products;
        try { products = JSON.parse(data.textContent); } catch (_) { return; }
        const handle = picker.dataset.currentProductHandle || picker.dataset.productHandle;
        if (!Array.isArray(products) || !products.some((product) => product.handle === handle && Array.isArray(product.variants) && product.variants.length)) return;
        instances.set(picker, new MixController(picker, productRoot, wrap, products, view));
      });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
    else init(document);
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      instances.forEach((instance, picker) => {
        if (event.target.contains(picker)) {
          instance.destroy();
          instances.delete(picker);
        }
      });
    });
    return { init, instances };
  }

  return { ensureSlots, needsCustomAdd, validateSelection, aggregateItems, calculateMixedOffer, confirmedItems, MixController, boot };
});
